import { useState, type FormEvent } from 'react';
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { createSecret } from '@/apps/apis/secrets';
import { SECRETS_QUERY_KEYS, useSecretsList } from '@/apps/apis/secrets/hooks';
import {
  SECRET_PATH_MAX_SEGMENTS,
  SECRET_RESERVED_FIRST_SEGMENT,
  SECRET_SEGMENT_PATTERN,
} from '@/apps/apis/secrets/types';
import type { SecretsMe } from '@/apps/apis/secrets/types';
import { ROUTES } from '@/routes/routes';
import { asArray, formatDate, readableError, SecretsGate } from './common';

/** Client-side mirror of the server path rule; the server stays authoritative. */
const isSecretPath = (path: string) => {
  const segments = path.split('/');
  return (
    segments.length <= SECRET_PATH_MAX_SEGMENTS &&
    segments[0] !== SECRET_RESERVED_FIRST_SEGMENT &&
    segments.every((segment) => SECRET_SEGMENT_PATTERN.test(segment))
  );
};

const NewSecretForm = () => {
  const [path, setPath] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedPath = path.trim();
    if (!isSecretPath(trimmedPath)) {
      setError(
        'Use 1-4 "/"-separated segments of lowercase letters, digits and "-" (e.g. prod/my-service/api); "system/" is reserved.',
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createSecret(trimmedPath);
      void queryClient.invalidateQueries({ queryKey: SECRETS_QUERY_KEYS.all });
      navigate(ROUTES.secrets.detail(created?.path ?? trimmedPath));
    } catch (caught) {
      setError(readableError(caught, 'Unable to create the secret.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={(event) => void submit(event)} className="d-flex gap-2 align-items-start">
      <div>
        <div className="d-flex gap-1 align-items-center">
          skinseoul/
          <Form.Control
            size="sm"
            placeholder="prod/my-service/api"
            aria-label="New secret path"
            value={path}
            onChange={(event) => setPath(event.target.value)}
          />
        </div>
        {error ? <div className="small text-danger mt-1">{error}</div> : null}
      </div>
      <Button size="sm" type="submit" disabled={saving}>
        New secret
      </Button>
    </Form>
  );
};

const SecretsList = ({ me }: { me: SecretsMe }) => {
  const secrets = useSecretsList();
  const items = asArray(secrets.data);

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-end gap-3 flex-wrap mb-3">
        <div>
          <h2 className="mb-1">Secrets</h2>
          <p className="text-muted mb-0">
            Managed service secrets. <Link to={ROUTES.secrets.requests}>Change requests</Link>
          </p>
        </div>
        {me.canManage ? <NewSecretForm /> : null}
      </div>

      {secrets.error ? <Alert variant="danger">{readableError(secrets.error, 'Unable to load secrets.')}</Alert> : null}

      <Card>
        <Card.Body>
          {secrets.isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : items.length === 0 ? (
            <p className="text-muted mb-0">No managed secrets.</p>
          ) : (
            <Table size="sm" responsive hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Secret</th>
                  <th>Fields</th>
                  <th>Updated</th>
                  <th>Pending requests</th>
                </tr>
              </thead>
              <tbody>
                {items.map((secret) => (
                  <tr key={secret?.path}>
                    <td>
                      <Link to={ROUTES.secrets.detail(secret?.path ?? '')}>{secret?.name ?? secret?.path ?? '—'}</Link>
                    </td>
                    <td>{asArray(secret?.fieldKeys).length}</td>
                    <td className="text-nowrap">{formatDate(secret?.updatedAt)}</td>
                    <td>
                      {(secret?.pendingRequestCount ?? 0) > 0 ? (
                        <Badge bg="warning">{secret.pendingRequestCount}</Badge>
                      ) : (
                        0
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

const SecretsListPage = () => <SecretsGate>{(me) => <SecretsList me={me} />}</SecretsGate>;

export default SecretsListPage;
